import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Use the Select component from the ui package instead
 */
export interface SelectOption {
    /** The value of the option */
    value: string;
    /** The label of the option */
    label: string;
    /** Whether the option is disabled (e.g. incompatible with the current target). */
    disabled?: boolean;
}

/**
 * Use the Select component from the ui package instead
 */
interface SelectFieldProps {
    /** The value of the select field */
    value?: string;
    /** The onChange event of the select field */
    onChange: (value: string) => void;
    /** The label of the select field */
    label: string;
    /** The error message of the select field */
    error?: string;
    /** The placeholder of the select field */
    placeholder?: string;
    /** The id of the select field */
    id?: string;
    /** The class name of the select field */
    className?: string;
    /** Whether the select field is required */
    required?: boolean;
    /** The options of the select field */
    options: SelectOption[];
    /** Whether the select field is disabled */
    disabled?: boolean;
    /** Whether the select field can be cleared */
    clearable?: boolean;
}

/**
 * Renders a select field with a label, error message, placeholder, and options
 */
export const SelectField = ({
    value,
    onChange,
    label,
    error,
    placeholder,
    id,
    className,
    required,
    options,
    disabled,
    clearable,
}: SelectFieldProps) => {
    const { t } = useTranslation();
    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(undefined!);
    };

    return (
        <div className={cn("space-y-2", className)}>
            <Label htmlFor={id} className="flex gap-0.5">
                {label}
                {required && <span className="text-destructive">*</span>}
            </Label>
            <div className="relative">
                <Select
                    defaultValue={value}
                    onValueChange={onChange}
                    disabled={disabled}
                    key={value}
                >
                    <SelectTrigger id={id}>
                        <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map((option) => (
                            <SelectItem
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled}
                            >
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {clearable && value && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-8 top-0 h-full"
                        onClick={handleClear}
                        disabled={disabled}
                    >
                        <X className="h-4 w-4" />
                        <span className="sr-only">{t("general.clear")}</span>
                    </Button>
                )}
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
    );
};
